import React from 'react'
import { Box, Card, CardContent } from '@mui/material'
import { SurveyQuestionType } from '../../../../models/Survey'
import LineChart from '../Charts/LineChart'
import MultiChoiceAndMultiSelectChart from '../Charts/MultiChoiceAndMultiSelectChart'
import BarChart from '../Charts/BarChart'
import Title from '../Charts/BarChart/Title'
import { statButtons } from '../constants'
import type { ICalculation, KeyStatSurveyItem, ReportsV2 } from '../../../../models/Report'
import StatisticBox from '../Charts/BarChart/StatisticBox'
import { ReportsActionType, useReportsContext } from '../../ReportsContext'
import StackedChartForMultiChoiceAndMultiSelect from '../Charts/StackedChartForMultiChoiceAndMultiSelect'
import StatComparisonChart from '../Charts/StatComparisonChart'
import { type ReportChartType } from '../../types'
import { type SurveyResponseMenuSelection } from '../Charts/BarChart'

interface SurveyReportChartProps {
  report: KeyStatSurveyItem
  surveyItem: KeyStatSurveyItem | null
  reports: ReportsV2
  calculations?: ICalculation[] | null
  comparisonCalculations?: ICalculation[] | null
  selectedTrend: number
  showSlice: boolean
  questionNumber: number
  sectionTitle: string
  surveySectionId?: string
  surveySectionRevision?: number
  instructorId?: string | null
  onFilterBySurveyResponse?: (selection: SurveyResponseMenuSelection) => void | Promise<void>
  onRemoveResponseFilterAnswer?: (answer: string) => void | Promise<void>
  highlightedResponseAnswers?: string[]
  responseFilterWarningMessage?: string | null
}

const SurveyReportChart: React.FC<SurveyReportChartProps> = ({
  report,
  surveyItem,
  reports,
  calculations = null,
  comparisonCalculations = null,
  selectedTrend,
  showSlice,
  questionNumber,
  sectionTitle,
  surveySectionId,
  surveySectionRevision,
  instructorId,
  onFilterBySurveyResponse,
  onRemoveResponseFilterAnswer,
  highlightedResponseAnswers = [],
  responseFilterWarningMessage = null
}) => {
  const {
    chartOrientation,
    globalChartType,
    questionChartOverrides,
    surveyVisibleKeyStats,
    displaySettings,
    dispatch
  } = useReportsContext()
  const [selectedStat, setSelectedStat] = React.useState(statButtons[0].value)
  const [cardChartOrientation, setCardChartOrientation] = React.useState<'vertical' | 'horizontal'>(chartOrientation)

  React.useEffect(() => {
    if (selectedTrend === 1) {
      setCardChartOrientation(chartOrientation)
    }
  }, [chartOrientation, selectedTrend])

  const questionType = report.itemType
  const isMultiSelectOrMultiChoice = questionType === SurveyQuestionType.MultiSelect || questionType === SurveyQuestionType.MultipleChoice
  const isLinearScaleQuestion = questionType === SurveyQuestionType.LinearScale
  const hasMultipleSeries = (reports?.titles?.length ?? 0) > 1
  const currentChartType = isLinearScaleQuestion ? (questionChartOverrides[report.itemId] ?? globalChartType) : 'distribution'
  const visibleKeyStats = surveyVisibleKeyStats
  const showQuestionChartTypeDropdown = selectedTrend === 1 && hasMultipleSeries && isLinearScaleQuestion
  const isDistributionChart = currentChartType === 'distribution' || !showQuestionChartTypeDropdown
  const statComparisonChartType = currentChartType as Exclude<ReportChartType, 'distribution'>
  const titleText = `${questionNumber}. ${report.prompt}`
  const isActiveResponseFilterTarget = displaySettings.responseFilter?.itemId === report.itemId

  const handleQuestionChartTypeChange = (chartType: ReportChartType): void => {
    dispatch({ type: ReportsActionType.SET_QUESTION_CHART_TYPE, payload: { questionId: report.itemId, chartType } })
  }

  let chart: React.ReactNode
  switch (true) {
    case selectedTrend > 2 && isMultiSelectOrMultiChoice:
      chart = <MultiChoiceAndMultiSelectChart report={report} questionTitle={titleText} titles={reports?.titles} />
      break
    case selectedTrend > 2:
      chart = (
        <LineChart
          report={report}
          titles={reports?.titles}
          titleComponent={<Title selectedTrend={selectedTrend} selectedStat={selectedStat} setSelectedStat={setSelectedStat} titleText={titleText} reports={reports} surveyItem={surveyItem} calculations={calculations ?? surveyItem?.calculations ?? []} comparisonCalculations={comparisonCalculations} visibleKeyStats={visibleKeyStats} />}
          selectedStat={selectedStat}
        />
      )
      break
    case selectedTrend === 1 && hasMultipleSeries && isDistributionChart:
      chart = (
        <Card>
          <CardContent>
            <Title
              titleText={titleText}
              reports={reports}
              surveyItem={surveyItem}
              calculations={calculations ?? surveyItem?.calculations ?? []}
              comparisonCalculations={comparisonCalculations}
              selectedTrend={selectedTrend}
              showOrientationToggle={true}
              chartOrientation={cardChartOrientation}
              onChartOrientationChange={setCardChartOrientation}
              selectedChartType={currentChartType}
              onChartTypeChange={handleQuestionChartTypeChange}
              showChartTypeDropdown={showQuestionChartTypeDropdown}
              visibleKeyStats={visibleKeyStats}
              responseFilterWarningMessage={isActiveResponseFilterTarget ? responseFilterWarningMessage : null}
            />
            <StackedChartForMultiChoiceAndMultiSelect
              titles={reports?.titles}
              report={report}
              isHorizontalLayout={cardChartOrientation === 'horizontal'}
            />
            <StatisticBox calculations={calculations ?? surveyItem?.calculations} comparisonCalculations={comparisonCalculations} visibleKeyStats={visibleKeyStats} />
          </CardContent>
        </Card>
      )
      break
    case selectedTrend === 1 && hasMultipleSeries && isLinearScaleQuestion:
      chart = (
        <StatComparisonChart
          titleComponent={
            <Title
              titleText={titleText}
              reports={reports}
              surveyItem={surveyItem}
              calculations={calculations ?? surveyItem?.calculations ?? []}
              comparisonCalculations={comparisonCalculations}
              selectedTrend={selectedTrend}
              showOrientationToggle={true}
              chartOrientation={cardChartOrientation}
              onChartOrientationChange={setCardChartOrientation}
              selectedChartType={currentChartType}
              onChartTypeChange={handleQuestionChartTypeChange}
              showChartTypeDropdown={showQuestionChartTypeDropdown}
              visibleKeyStats={visibleKeyStats}
              responseFilterWarningMessage={isActiveResponseFilterTarget ? responseFilterWarningMessage : null}
            />
          }
          statisticBox={<StatisticBox calculations={calculations ?? surveyItem?.calculations} comparisonCalculations={comparisonCalculations} visibleKeyStats={visibleKeyStats} />}
          report={report}
          titles={reports?.titles}
          chartType={statComparisonChartType}
          isHorizontalLayout={cardChartOrientation === 'horizontal'}
        />
      )
      break
    default:
      chart = (
        <BarChart
          chartKey={questionNumber}
          report={report}
          showPercents={true}
          showSlice={showSlice}
          titles={reports?.titles}
          questionNumber={questionNumber}
          sectionTitle={sectionTitle}
          surveySectionId={surveySectionId}
          surveySectionRevision={surveySectionRevision}
          instructorId={instructorId}
          onFilterBySurveyResponse={onFilterBySurveyResponse}
          onRemoveResponseFilterAnswer={onRemoveResponseFilterAnswer}
          highlightedResponseAnswers={highlightedResponseAnswers}
          titleComponent={
            <Title
              titleText={titleText}
              reports={reports}
              surveyItem={surveyItem}
              calculations={calculations ?? surveyItem?.calculations ?? []}
              comparisonCalculations={comparisonCalculations}
              selectedTrend={selectedTrend}
              showOrientationToggle={selectedTrend === 1}
              chartOrientation={cardChartOrientation}
              onChartOrientationChange={setCardChartOrientation}
              selectedChartType={currentChartType}
              visibleKeyStats={visibleKeyStats}
              responseFilterWarningMessage={isActiveResponseFilterTarget ? responseFilterWarningMessage : null}
            />
          }
          statisticBox={<StatisticBox calculations={calculations ?? surveyItem?.calculations} comparisonCalculations={comparisonCalculations} visibleKeyStats={visibleKeyStats} />}
          isHorizontalLayout={selectedTrend === 1 ? cardChartOrientation === 'horizontal' : undefined}
        />
      )
  }

  return (
    <Box key={questionNumber} sx={{ mb: 3 }}>
      {chart}
    </Box>
  )
}

export default SurveyReportChart
